<template>
  <simulador-card
    :title="$t('tools.investimentoTitle')"
    :info="$t('tools.investimentoInfo')"
  >
    <div class="payback-container">
      <simulador-field
        id="tempo-retorno-investimento"
        :label="$t('tools.paybackLabel')"
        type="number"
        :value="tempoRetorno"
        :min="1"
        :step="1"
        unit="meses"
        @input="atualizarPayback"
      />

      <simulador-field
        id="investimento-necessario"
        :label="$t('tools.investimentoLabel')"
        :value="investimento"
        unit="R$"
        unit-position="left"
        :formula="$t('tools.investimentoFormula')"
        :is-result="true"
      />
    </div>
  </simulador-card>
</template>

<script>
import SimuladorCard from "./SimuladorCard.vue";
import SimuladorField from "./SimuladorField.vue";

export default {
  name: "Investimento",
  components: {
    SimuladorCard,
    SimuladorField
  },
  props: {
    value: {
      type: Object,
      default: () => ({
        tempoRetornoInvestimento: 12,
        investimentoNecessario: "0,00"
      })
    },
    lucroProjetado: {
      type: [Number, String],
      default: 0
    }
  },
  data() {
    return {
      tempoRetorno: 12,
      investimento: "0,00",
      mounted: false // Flag para controlar inicialização
    };
  },
  methods: {
    atualizarPayback(valor) {
      this.tempoRetorno = valor;
      this.calcular();
    },
    formatarMoeda(numero) {
      if (typeof numero !== "number" || isNaN(numero)) {
        numero = 0;
      }
      return numero.toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
    calcular() {
      // Obter o valor do lucro projetado da prop
      let lucroValor = 0;

      // Verificar se é string ou número e fazer a conversão adequada
      if (typeof this.lucroProjetado === "string") {
        // Remover caracteres não numéricos, exceto vírgula e ponto
        const valorLimpo = this.lucroProjetado.replace(/[R$\s]/g, "");

        // Converter para formato que JavaScript entende
        // Primeiro remover os separadores de milhar (pontos)
        const semPontos = valorLimpo.replace(/\./g, "");

        // Substituir vírgula por ponto para decimais
        const comPonto = semPontos.replace(",", ".");

        // Agora converter para número
        lucroValor = parseFloat(comPonto) || 0;
      } else if (typeof this.lucroProjetado === "number") {
        lucroValor = this.lucroProjetado;
      }

      // Verificar se o valor é válido
      if (isNaN(lucroValor) || lucroValor <= 0) {
        // Tentar extrair todos os dígitos como último recurso
        if (typeof this.lucroProjetado === "string") {
          const apenasDigitos = this.lucroProjetado.match(/\d+/g);

          if (apenasDigitos && apenasDigitos.length > 0) {
            // Juntar todos os dígitos e considerar os 2 últimos como centavos
            const valorString = apenasDigitos.join("");
            const valor = parseFloat(valorString) / 100;
            lucroValor = valor;
          }
        }

        // Se ainda for inválido, usar um valor mínimo para evitar quebrar a aplicação
        if (isNaN(lucroValor) || lucroValor <= 0) {
          lucroValor = 1; // Valor mínimo para evitar erros
        }
      }

      const meses = parseFloat(this.tempoRetorno) || 12;

      // Cálculo: lucro projetado × meses (Lucro × Payback)
      const resultado = lucroValor * meses;

      // Atualizar valor formatado
      this.investimento = this.formatarMoeda(resultado);

      // Emitir eventos
      this.$emit("input", {
        tempoRetornoInvestimento: meses,
        investimentoNecessario: this.investimento
      });

      this.$emit("investimento-atualizado", resultado);
      return resultado;
    },
    forcaCalculoInicial() {
      // Garantir que o componente esteja marcado como montado
      this.mounted = true;

      // Verificar se temos um valor de lucroProjetado, se não, usar valor padrão
      this.calcular();
    }
  },
  mounted() {
    // Inicializar valor
    this.tempoRetorno = this.value.tempoRetornoInvestimento || 12;

    // Marcar como montado
    this.mounted = true;

    // Forçar o cálculo inicial após um pequeno delay para permitir que outros componentes sejam inicializados
    this.$nextTick(() => {
      this.calcular();
    });
  },
  watch: {
    // Adicionar watchers para as props
    lucroProjetado: {
      handler(newVal) {
        this.calcular();
      },
      immediate: true
    },
    value: {
      handler(newVal) {
        if (newVal && newVal.tempoRetornoInvestimento) {
          this.tempoRetorno = newVal.tempoRetornoInvestimento;
        }
      },
      deep: true,
      immediate: true
    }
  }
};
</script>

<style scoped>
.payback-container {
  width: 100%;
  display: flex;
  flex-wrap: wrap;
}

.payback-container :deep(.field-group) {
  width: 50%;
  padding-right: 15px;
  box-sizing: border-box;
}

@media screen and (max-width: 768px) {
  .payback-container :deep(.field-group) {
    width: 100%;
    padding-right: 0;
  }
}
</style>
